import { observer } from 'mobx-react';
import { useVirtualizer } from '@tanstack/react-virtual';

import EmptyPlaceholder from '@/common/components/EmptyPlaceholder/EmptyPlaceholder';
import Loader from '@/common/components/Loader/Loader';
import { useVirtualScrollArea } from '@/common/hooks/useVirtualScrollArea/useVirtualScrollArea';
import { DriveTreeItem } from '@/drive/entities/DriveTreeItem/DriveTreeItem';
import RecentDestinationItem from '@/forwarding/components/ForwardModal/ForwardModalBody/RecentDestinations/RecentDestinationItem/RecentDestinationItem';
import { useRecentDestinationsStore } from '@/forwarding/components/ForwardModal/ForwardModalBody/RecentDestinations/RecentDestinationsStore';
import RecentShareActionItem from '@/forwarding/components/ForwardModal/ForwardModalBody/RecentDestinations/RecentShareActionItem/RecentShareActionItem';
import { isShareActionType, ShareActionType } from '@/forwarding/constants/ShareActionType';

import styles from './RecentDestinations.module.css';
import { getRecentDestinationKey } from './RecentDestinations.utils';

export interface RecentDestinationsProps {
  onSelect: (destination: DriveTreeItem) => void;
  onShareActionSelect: (action: ShareActionType) => void;
}

const ESTIMATED_ROW_HEIGHT = 64;
const OVERSCAN_ROWS = 8;

function RecentDestinations(props: RecentDestinationsProps) {
  const { onSelect, onShareActionSelect } = props;
  const { isLoading, destinations, selected } = useRecentDestinationsStore();

  const [listRef, { scrollElement, scrollMargin }] = useVirtualScrollArea();

  const virtualizer = useVirtualizer({
    count: destinations?.length ?? 0,
    getScrollElement: () => scrollElement,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    getItemKey: (index) => (destinations ? getRecentDestinationKey(destinations[index]) : index),
    overscan: OVERSCAN_ROWS,
    scrollMargin,
  });

  if (isLoading) {
    return <Loader />;
  }

  if (!destinations || destinations.length === 0) {
    return <EmptyPlaceholder className={styles.emptyPlaceholder}>No destinations match your search.</EmptyPlaceholder>;
  }

  return (
    <div className={styles.recentDestinations} ref={listRef} style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map(({ index, start }) => {
        const destination = destinations[index];

        return (
          <div
            className={styles.row}
            key={getRecentDestinationKey(destination)}
            data-index={index}
            ref={virtualizer.measureElement}
            style={{ transform: `translateY(${start - scrollMargin}px)` }}
          >
            {isShareActionType(destination) ? (
              <RecentShareActionItem
                action={destination}
                ariaLabel={`share-action-${destination}`}
                onSelect={() => onShareActionSelect(destination)}
              />
            ) : (
              <RecentDestinationItem
                item={destination}
                ariaLabel={`destination-${destination.id}`}
                isSelected={selected?.id === destination.id}
                onSelect={() => onSelect(destination)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default observer(RecentDestinations);
